import { Suspense, lazy, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';
import Home from './pages/Home';
const Store = lazy(() => import('./pages/Store'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Gallery = lazy(() => import('./pages/Gallery'));
const GalleryDetail = lazy(() => import('./pages/GalleryDetail'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Artists = lazy(() => import('./pages/Artists'));
const ArtistDetail = lazy(() => import('./pages/ArtistDetail'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const OpenCall = lazy(() => import('./pages/OpenCall'));
const ErrorPage = lazy(() => import('./pages/ErrorPage'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminNewPost = lazy(() => import('./pages/admin/AdminNewPost'));
const AdminEditPost = lazy(() => import('./pages/admin/AdminEditPost'));
const AdminPages = lazy(() => import('./pages/admin/AdminPages'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminGallery = lazy(() => import('./pages/admin/AdminGallery'));
const AdminArtists = lazy(() => import('./pages/admin/AdminArtists'));
const AdminOpenCall = lazy(() => import('./pages/admin/AdminOpenCall'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));

const LazyPage = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<div className="min-h-[40vh] flex items-center justify-center text-gray-500">Loading...</div>}>
    {children}
  </Suspense>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <LazyPage><ErrorPage /></LazyPage>,
    children: [
      { index: true, element: <Home /> },
      { path: 'gallery', element: <LazyPage><Gallery /></LazyPage> },
      { path: 'gallery/:id', element: <LazyPage><GalleryDetail /></LazyPage> },
      { path: 'store', element: <LazyPage><Store /></LazyPage> },
      { path: 'checkout/:productId', element: <LazyPage><Checkout /></LazyPage> },
      { path: 'blog', element: <LazyPage><Blog /></LazyPage> },
      { path: 'blog/:slug', element: <LazyPage><BlogPost /></LazyPage> },
      { path: 'artists', element: <LazyPage><Artists /></LazyPage> },
      { path: 'artists/:id', element: <LazyPage><ArtistDetail /></LazyPage> },
      { path: 'about', element: <LazyPage><About /></LazyPage> },
      { path: 'contact', element: <LazyPage><Contact /></LazyPage> },
      { path: 'open-call', element: <LazyPage><OpenCall /></LazyPage> },
    ],
  },
  {
    path: '/admin',
    children: [
      { path: 'login', element: <LazyPage><AdminLogin /></LazyPage> },
      {
        path: '',
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/admin/blog" replace /> },
          { path: 'dashboard', element: <Navigate to="/admin/blog" replace /> },
          { path: 'blog', element: <LazyPage><AdminDashboard /></LazyPage> },
          { path: 'blog/new', element: <LazyPage><AdminNewPost /></LazyPage> },
          { path: 'blog/edit/:id', element: <LazyPage><AdminEditPost /></LazyPage> },
          { path: 'gallery', element: <LazyPage><AdminGallery /></LazyPage> },
          { path: 'artists', element: <LazyPage><AdminArtists /></LazyPage> },
          { path: 'products', element: <LazyPage><AdminProducts /></LazyPage> },
          { path: 'orders', element: <LazyPage><AdminOrders /></LazyPage> },
          { path: 'open-call', element: <LazyPage><AdminOpenCall /></LazyPage> },
          { path: 'pages', element: <LazyPage><AdminPages /></LazyPage> },
          { path: 'settings', element: <LazyPage><AdminSettings /></LazyPage> },
        ],
      },
    ],
  },
]);

export default function Router() {
  return <RouterProvider router={router} />;
}